import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Mic } from 'lucide-react';
import api from '@/lib/axios';
import { useActionBar } from '@/contexts/ActionBarContext';
import { getFooterSettings } from '@/components/FooterDateBar';
import {
  executeVoicePlan,
  getSpeechRecognitionCtor,
  getVoiceSearchCriteria,
  isHandsFreeEnabled,
  setHandsFreeEnabled,
  speakText,
  stripWakePhrase,
  VOICE_START_EVENT,
} from '@/lib/voiceControl';
import {
  fetchRealtimeStatus,
  realtimeBlocker,
  startRealtimeSession,
} from '@/lib/voiceRealtime';
import { createRealtimeToolRunner, pathForMissingData } from '@/lib/voiceRealtimeTools';

function isTypingTarget(el) {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
}

/** Layout stellt diesen Platz in der Kopfzeile bereit. */
export const VOICE_STATUS_SLOT_ID = 'voice-status-slot';

export default function VoiceController() {
  const navigate = useNavigate();
  const location = useLocation();
  const [slot, setSlot] = useState(null);
  const { config, requestAction } = useActionBar();
  const [handsFree, setHandsFree] = useState(() => isHandsFreeEnabled());
  const [status, setStatus] = useState('idle'); // idle | listening | processing
  const [transcript, setTranscript] = useState('');
  const [spoken, setSpoken] = useState('');
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState('');
  // Stehende Ursache, warum es schlechter läuft als möglich — etwa ein fehlender
  // Schlüssel. Anders als `error` überlebt sie den nächsten Bedienschritt, sonst
  // würde `startCommand`/`startRec` sie sofort wieder wegwischen.
  const [hint, setHint] = useState('');
  const [armed, setArmed] = useState(false);
  // Laufendes Gespräch (WebRTC). Solange es steht, wird die alte Einzelsatz-Kette
  // nicht angefasst.
  const [talking, setTalking] = useState(false);
  const sessionRef = useRef(null);
  const recRef = useRef(null);
  const armedRef = useRef(false);
  const commandModeRef = useRef(false);
  const followUpUntilRef = useRef(0);
  const lastHeardRef = useRef({ text: '', at: 0 });
  const ttsAudioRef = useRef(null);
  const startRecRef = useRef(null);
  const statusRef = useRef(status);
  const handsFreeRef = useRef(handsFree);
  // Ein Gespräch überlebt Seitenwechsel, die Werkzeuge brauchen deshalb den
  // aktuellen Pfad und nicht den vom Sitzungsbeginn.
  const pathnameRef = useRef(location.pathname);
  statusRef.current = status;
  handsFreeRef.current = handsFree;
  pathnameRef.current = location.pathname;

  const stopTts = useCallback(() => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    const audio = ttsAudioRef.current;
    ttsAudioRef.current = null;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
    }
  }, []);

  const speakAudio = useCallback(async (text) => {
    let res;
    try {
      res = await api.post('/voice/speak', { text }, { responseType: 'blob' });
    } catch (err) {
      // Ohne Schlüssel spricht sonst stillschweigend die Systemstimme des Rechners
      // — die klingt wie ein Navi und niemand erfährt, woran es liegt.
      if (err?.response?.status === 503) {
        setHint('Kein OpenAI-Schlüssel hinterlegt — es spricht die Systemstimme. Einstellungen → KI.');
      }
      throw err;
    }
    const blob = res.data;
    if (!blob || blob.size < 40) throw new Error('empty tts');
    const url = URL.createObjectURL(blob);
    await new Promise((resolve, reject) => {
      const audio = new Audio(url);
      ttsAudioRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
      audio.onerror = () => { URL.revokeObjectURL(url); reject(new Error('play')); };
      audio.play().catch(reject);
    });
  }, []);

  const stopRec = useCallback(() => {
    const rec = recRef.current;
    recRef.current = null;
    try { rec?.abort(); } catch { /* ignore */ }
  }, []);

  const speak = useCallback((text, { asAnswer = false, onEnd } = {}) => {
    stopRec();
    setSpoken(text || '');
    if (asAnswer) setAnswer(text || '');
    speakText(text, { onEnd, speakAudio });
  }, [speakAudio, stopRec]);

  const stopVoice = useCallback((announce = true) => {
    const session = sessionRef.current;
    sessionRef.current = null;
    try { session?.stop(); } catch { /* ignore */ }
    setTalking(false);
    commandModeRef.current = false;
    followUpUntilRef.current = 0;
    setHandsFree(false);
    setHandsFreeEnabled(false);
    armedRef.current = false;
    setArmed(false);
    stopRec();
    stopTts();
    setStatus('idle');
    setTranscript('');
    setAnswer('');
    setHint('');
    if (announce) setSpoken('Sprachsteuerung aus.');
  }, [stopRec, stopTts]);

  const listenAfterSpeak = useCallback(() => {
    if (!armedRef.current) return;
    if (Date.now() < followUpUntilRef.current && startRecRef.current) {
      commandModeRef.current = true;
      startRecRef.current(true);
    }
  }, []);

  const runPlan = useCallback(async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) return;
    setStatus('processing');
    setTranscript(trimmed);
    try {
      const { data } = await api.post('/voice/interpret', {
        transcript: trimmed,
        pathname: location.pathname + (location.search || ''),
        selected: config?.selected || null,
        criteria: getVoiceSearchCriteria(),
        footer: getFooterSettings(),
      });
      if (data?.intent === 'stop' || data?.intent === 'handsfree_off') {
        executeVoicePlan(data, {
          navigate,
          requestAction,
          setHandsFree: (on) => {
            setHandsFree(on);
            setHandsFreeEnabled(on);
          },
        });
        stopVoice(false);
        setSpoken(data.speak || 'Sprachsteuerung aus.');
        return;
      }
      executeVoicePlan(data, {
        navigate,
        requestAction,
        onNew: config?.onNew,
        newMenu: config?.newMenu,
        setHandsFree: (on) => {
          setHandsFree(on);
          setHandsFreeEnabled(on);
        },
      });
      if (data?.intent === 'handsfree_on') setHandsFree(true);
      followUpUntilRef.current = Date.now() + 20000;
      commandModeRef.current = true;
      const line = data?.speak || data?.llm_error;
      if (line) speak(line, { asAnswer: true, onEnd: listenAfterSpeak });
      else listenAfterSpeak();
    } catch {
      setError('Befehl konnte nicht verstanden werden');
      followUpUntilRef.current = Date.now() + 20000;
      speak('Das hat nicht geklappt.', { asAnswer: true, onEnd: listenAfterSpeak });
    } finally {
      setStatus('idle');
    }
  }, [config?.newMenu, config?.onNew, config?.selected, listenAfterSpeak, location.pathname, location.search, navigate, requestAction, speak, stopVoice]);

  const handleHeard = useCallback((raw) => {
    const { wakeOnly, command, needsWake } = stripWakePhrase(raw);
    const now = Date.now();
    const key = String(raw || '').trim().toLowerCase();
    if (key && lastHeardRef.current.text === key && now - lastHeardRef.current.at < 2500) {
      return;
    }
    if (key) lastHeardRef.current = { text: key, at: now };
    if (wakeOnly) {
      commandModeRef.current = true;
      followUpUntilRef.current = now + 15000;
      armedRef.current = true;
      setArmed(true);
      setTranscript('MAMV');
      speak('Ja?', { onEnd: listenAfterSpeak });
      return;
    }
    const inFollowUp = now < followUpUntilRef.current;
    if (needsWake && handsFreeRef.current && !commandModeRef.current && !inFollowUp) {
      return;
    }
    runPlan(command || raw);
  }, [listenAfterSpeak, runPlan, speak]);

  const startRec = useCallback((commandMode) => {
    const Rec = getSpeechRecognitionCtor();
    if (!Rec) {
      setError('Dieser Browser unterstützt keine Spracheingabe (Chrome oder Safari).');
      return;
    }
    stopRec();
    commandModeRef.current = !!commandMode;
    const rec = new Rec();
    rec.lang = 'de-DE';
    rec.interimResults = true;
    rec.continuous = handsFreeRef.current && !commandMode;
    rec.onresult = (ev) => {
      let finalText = '';
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
        const piece = ev.results[i][0]?.transcript || '';
        if (ev.results[i].isFinal) finalText += piece;
        else interim += piece;
      }
      if (interim) setTranscript(interim);
      if (finalText.trim()) handleHeard(finalText.trim());
    };
    rec.onerror = (ev) => {
      if (ev.error === 'not-allowed') {
        setError('Mikrofonzugriff fehlt — einmal im Browser erlauben, danach geht alles per Taste oder MAMV.');
      }
    };
    rec.onend = () => {
      if (recRef.current !== rec) return;
      recRef.current = null;
      if (handsFreeRef.current && statusRef.current !== 'processing') {
        startRec(false);
      } else if (statusRef.current === 'listening') {
        setStatus('idle');
      }
    };
    recRef.current = rec;
    try {
      rec.start();
      setStatus('listening');
      setError('');
    } catch {
      /* already started */
    }
  }, [handleHeard, stopRec]);

  const startCommand = useCallback(() => {
    setError('');
    setTranscript('');
    armedRef.current = true;
    setArmed(true);
    commandModeRef.current = true;
    followUpUntilRef.current = Date.now() + 20000;
    speak('Was brauchst du?', { onEnd: listenAfterSpeak });
  }, [listenAfterSpeak, speak]);

  /**
   * Erst das echte Gespräch versuchen. Nur wenn das nicht geht, die alte
   * Einzelsatz-Kette — und dann mit Begründung, damit niemand rätselt, warum es
   * plötzlich nach Navi klingt.
   */
  const startVoice = useCallback(async () => {
    if (sessionRef.current) return;
    const blocked = realtimeBlocker();
    if (blocked) {
      startCommand();
      setHint(blocked);
      return;
    }
    setError('');
    setHint('');
    setSpoken('');
    setAnswer('');
    armedRef.current = true;
    setArmed(true);
    setStatus('processing');
    try {
      const status = await fetchRealtimeStatus();
      if (!status?.available) {
        startCommand();
        setHint(status?.reason || 'Sprachgespräch nicht verfügbar.');
        return;
      }
      const session = await startRealtimeSession({
        pathname: location.pathname + (location.search || ''),
        runTool: createRealtimeToolRunner({
          navigate,
          stopVoice: () => stopVoice(false),
          getPathname: () => pathnameRef.current,
        }),
        onState: (state) => {
          if (state === 'closed') {
            sessionRef.current = null;
            setTalking(false);
            setStatus('idle');
            return;
          }
          setTalking(true);
          setStatus(state === 'connecting' ? 'processing' : 'listening');
          if (state === 'speaking') setTranscript('');
        },
        onUserText: (text, final) => {
          if (final) {
            setTranscript(text);
            const path = pathForMissingData(text);
            if (path) navigate(path);
          } else setTranscript((cur) => `${cur}${text}`);
        },
        onAssistantText: (text, final) => {
          if (final) { setSpoken(text); setAnswer(text); }
          else setAnswer((cur) => `${cur}${text}`);
        },
        onError: (message) => setError(message),
      });
      sessionRef.current = session;
      setTalking(true);
      if (session.limited) {
        setHint('Gespräch läuft, aber ohne Zugriff auf die Daten — bitte melden.');
      }
    } catch (err) {
      startCommand();
      setHint(String(err?.message || err || 'Sprachgespräch fehlgeschlagen'));
    }
  }, [location.pathname, location.search, navigate, startCommand, stopVoice]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        stopVoice();
        return;
      }
      const f8 = e.key === 'F8';
      const combo = (e.altKey && e.code === 'Space') || (e.ctrlKey && e.shiftKey && e.code === 'Space');
      const toggleHf = e.altKey && e.shiftKey && (e.key === 'v' || e.key === 'V');
      if (toggleHf) {
        e.preventDefault();
        const next = !handsFreeRef.current;
        setHandsFree(next);
        setHandsFreeEnabled(next);
        speak(next ? 'Dauerhören ein. Sag MAMV und dann den Befehl.' : 'Dauerhören aus.');
        if (next) startRec(false);
        else stopRec();
        return;
      }
      if (!f8 && !combo) return;
      if (combo && isTypingTarget(e.target) && !e.altKey) return;
      e.preventDefault();
      if (statusRef.current === 'listening' || statusRef.current === 'processing') {
        stopVoice();
        return;
      }
      startVoice();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [speak, startRec, startVoice, stopVoice]);

  useEffect(() => {
    const onToolbar = () => {
      if (statusRef.current === 'listening' || statusRef.current === 'processing') {
        stopVoice();
        return;
      }
      startVoice();
    };
    window.addEventListener(VOICE_START_EVENT, onToolbar);
    return () => window.removeEventListener(VOICE_START_EVENT, onToolbar);
  }, [startVoice, stopVoice]);

  startRecRef.current = startRec;

  useEffect(() => {
    // Läuft ein Gespräch, hört OpenAI schon mit — die Browser-Erkennung würde
    // dasselbe Mikrofon ein zweites Mal belegen.
    if (sessionRef.current) return undefined;
    if (!getSpeechRecognitionCtor() || !handsFree) return undefined;
    let cancelled = false;
    const boot = async () => {
      try {
        if (!navigator.permissions?.query) return;
        const perm = await navigator.permissions.query({ name: 'microphone' });
        if (cancelled || perm.state !== 'granted') return;
        startRecRef.current(false);
      } catch {
        /* Safari / Permissions-API */
      }
    };
    boot();
    return () => {
      cancelled = true;
      stopRec();
    };
  }, [handsFree, stopRec]);

  useEffect(() => () => {
    stopRec();
    try { sessionRef.current?.stop(); } catch { /* ignore */ }
    sessionRef.current = null;
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  }, [stopRec]);

  const listening = status === 'listening';
  const processing = status === 'processing';
  const active = listening || processing || armed || handsFree || talking;

  // Der Slot fehlt auf Seiten mit eigener Kopfzeile, deshalb bei jedem
  // Seitenwechsel und vor dem Einblenden neu nachsehen.
  useLayoutEffect(() => {
    setSlot(document.getElementById(VOICE_STATUS_SLOT_ID));
  }, [location.pathname, active, spoken, answer, error]);

  // Restmeldungen im Ruhezustand von selbst ausblenden, damit die Leiste nicht
  // dauerhaft über der Fußzeile stehen bleibt.
  useEffect(() => {
    if (!spoken && !answer && !error && !hint) return undefined;
    if (listening || processing || armed) return undefined;
    const timer = setTimeout(() => {
      setSpoken('');
      setAnswer('');
      setError('');
      // Auch der Hinweis geht mit: eine Meldung, die nie verschwindet, war schon
      // einmal das Problem.
      setHint('');
    }, 6000);
    return () => clearTimeout(timer);
  }, [listening, processing, armed, spoken, answer, error, hint]);

  const statusText = error
    || (processing && (transcript || (talking ? 'Verbinde…' : 'Denke nach…')))
    || (listening && (transcript || (talking
      ? 'Sprich einfach — du kannst dazwischenreden'
      : (commandModeRef.current ? 'Nächste Frage oder Aus…' : 'Sprich deinen Befehl…'))))
    || spoken
    || (handsFree ? 'Dauerhören' : '');

  // Solange die Sprachsteuerung aus ist, gibt es nichts zu melden — Einstieg
  // sind der Mikrofon-Knopf in der Kopfzeile und F8.
  if (!active && !spoken && !answer && !error && !hint) return null;

  const bar = (
    <div
      className={`min-w-0 ${slot ? 'w-full' : 'w-[min(36rem,calc(100vw-2rem))]'} flex flex-col`}
      data-testid="voice-controller"
      aria-live="polite"
    >
      <div className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs min-w-0 ${
        listening
          ? 'bg-brand-red text-white border-brand-red'
          : processing
            ? 'bg-brand-anthracite text-white border-brand-anthracite'
            : 'bg-white text-slate-600 border-slate-200'
      }`}
      >
        {processing ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
        ) : (
          <Mic className={`w-3.5 h-3.5 shrink-0 ${listening ? 'animate-pulse' : ''}`} />
        )}
        <span className="truncate" data-testid="voice-controller-status">
          {statusText}
        </span>
        {active && (
          <button
            type="button"
            data-testid="voice-controller-stop"
            className={`shrink-0 rounded-full px-2 py-0.5 font-semibold ${
              listening || processing ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'
            }`}
            onClick={() => stopVoice()}
          >
            Aus
          </button>
        )}
      </div>
      {hint && (
        <div
          data-testid="voice-controller-hint"
          className="mt-1 rounded-lg bg-amber-50 px-3 py-1 text-[11px] text-amber-900 border border-amber-200"
        >
          {hint}
        </div>
      )}
      {answer && !error && (
        <div
          data-testid="voice-controller-answer"
          className={`rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-lg ${
            slot ? 'absolute top-full left-0 right-0 mt-1 z-[70]' : 'mt-2 w-full'
          }`}
        >
          {answer}
        </div>
      )}
    </div>
  );

  // In der Kopfzeile ist Platz und die Meldung verdeckt nichts. Fehlt der Slot
  // (Seiten mit eigener Kopfzeile), oben einblenden — nie über der Fußzeile.
  if (slot) return createPortal(bar, slot);
  return (
    <div className="fixed top-2 left-1/2 z-[60] -translate-x-1/2 flex justify-center">
      {bar}
    </div>
  );
}
